import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

/**
 * Composable, theme-aware loading skeletons.
 *
 * Use these inside a page's own loading state (gated on `isLoading`/`isPending`)
 * so components fade into a placeholder of the *same shape* instead of popping in
 * from blank. All pieces use semantic tokens (`bg-muted`, `bg-card`, `border`),
 * so they respect the active theme and distributor branding.
 */

/** A row of metric/stat cards. */
/**
 * Tailwind needs whole class names at build time, so these cannot be built by
 * interpolation.
 */
const COLUMNS: Record<number, string> = {
  1: "lg:grid-cols-1",
  2: "lg:grid-cols-2",
  3: "lg:grid-cols-3",
  4: "lg:grid-cols-4",
  5: "lg:grid-cols-5",
  6: "lg:grid-cols-6",
};

export function StatCardsSkeleton({
  count = 4,
  className,
}: {
  count?: number;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "grid gap-4 sm:grid-cols-2",
        // The column count follows `count`. Pinned at four, a `count={5}` wrapped
        // the fifth card onto a second row — so a page asking for five cards drew
        // a skeleton a whole row taller than the router's, and the handover
        // between them read as two different loading screens rather than one
        // continuing.
        COLUMNS[count] ?? "lg:grid-cols-4",
        className,
      )}
    >
      {/* Mirrors StatCard's box model exactly (px-4 py-3, 12/20/12px type
          stack). A skeleton taller than the thing it stands in for makes the
          grid jump on load, which is the one job it has. */}
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="space-y-1.5 rounded-xl border bg-card px-4 py-3"
        >
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-5 w-20" />
          <Skeleton className="h-3 w-28" />
        </div>
      ))}
    </div>
  );
}

/** A bordered table/list card with an optional header and N rows. */
export function TableSkeleton({
  rows = 6,
  cols = 4,
  header = true,
  className,
}: {
  rows?: number;
  cols?: number;
  header?: boolean;
  className?: string;
}) {
  return (
    <div className={cn("rounded-xl border bg-card", className)}>
      {header && (
        <div className="border-b p-4">
          <Skeleton className="h-5 w-40" />
        </div>
      )}
      <div className="divide-y">
        {Array.from({ length: rows }).map((_, r) => (
          <div key={r} className="flex items-center gap-4 p-4">
            <Skeleton className="h-9 w-9 shrink-0 rounded-lg" />
            <div
              className="grid flex-1 gap-2"
              style={{
                gridTemplateColumns: `repeat(${Math.max(cols - 1, 1)}, minmax(0,1fr))`,
              }}
            >
              {Array.from({ length: Math.max(cols - 1, 1) }).map((_, c) => (
                <Skeleton
                  key={c}
                  className={cn("h-4", c === 0 ? "w-3/4" : "w-1/2")}
                />
              ))}
            </div>
            <Skeleton className="h-8 w-16 shrink-0 rounded-md" />
          </div>
        ))}
      </div>
    </div>
  );
}

/** A responsive grid of content cards. */
export function CardGridSkeleton({
  count = 6,
  columns = 3,
  className,
}: {
  count?: number;
  columns?: 2 | 3 | 4;
  className?: string;
}) {
  const cols = {
    2: "sm:grid-cols-2",
    3: "sm:grid-cols-2 lg:grid-cols-3",
    4: "sm:grid-cols-2 lg:grid-cols-4",
  }[columns];
  return (
    <div className={cn("grid gap-4", cols, className)}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="space-y-3 rounded-xl border bg-card p-5">
          <div className="flex items-center gap-3">
            <Skeleton className="h-10 w-10 shrink-0 rounded-lg" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-3 w-1/3" />
            </div>
          </div>
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-5/6" />
          <div className="flex gap-2 pt-1">
            <Skeleton className="h-8 w-20 rounded-md" />
            <Skeleton className="h-8 w-20 rounded-md" />
          </div>
        </div>
      ))}
    </div>
  );
}

// Fixed bar heights — deterministic (no Math.random in render) so the skeleton
// doesn't jitter between paints.
const BAR_HEIGHTS = [52, 78, 40, 88, 64, 96, 48, 72, 58, 84, 44, 68];

/**
 * A chart placeholder that draws shimmering bars + a legend rather than a flat
 * block — reads as a real chart loading. `bars` controls the column count.
 */
export function ChartSkeleton({
  className,
  height = "h-64",
  bars = 9,
  legend = 3,
}: {
  className?: string;
  height?: string;
  bars?: number;
  legend?: number;
}) {
  return (
    <div className={cn("space-y-4 rounded-xl border bg-card p-5", className)}>
      <div className="flex items-center justify-between">
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-8 w-24 rounded-md" />
      </div>
      <div className={cn("flex items-end gap-2", height)}>
        {Array.from({ length: bars }).map((_, i) => (
          <Skeleton
            key={i}
            className="flex-1 rounded-t-md"
            style={{ height: `${BAR_HEIGHTS[i % BAR_HEIGHTS.length]}%` }}
          />
        ))}
      </div>
      {legend > 0 && (
        <div className="flex flex-wrap justify-center gap-4 pt-1">
          {Array.from({ length: legend }).map((_, i) => (
            <div key={i} className="flex items-center gap-2">
              <Skeleton className="h-3 w-3 rounded-full" />
              <Skeleton className="h-3 w-16" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** A simple vertical list of lines (filters, side panels, settings rows). */
export function ListSkeleton({
  rows = 5,
  className,
}: {
  rows?: number;
  className?: string;
}) {
  return (
    <div className={cn("space-y-3", className)}>
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="flex items-center justify-between gap-4 rounded-lg border bg-card p-4"
        >
          <div className="space-y-2">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-3 w-56 max-w-full" />
          </div>
          <Skeleton className="h-6 w-11 rounded-full shrink-0" />
        </div>
      ))}
    </div>
  );
}

/** Page title + subtitle block. */
export function HeaderSkeleton({ className }: { className?: string }) {
  return (
    <div className={cn("space-y-1.5", className)}>
      {/* 32 + 6 + 20 = 58px: the real header's h1, its gap and one text-sm
          line, so a page does not move by the difference when it mounts. */}
      <Skeleton className="h-8 w-56" />
      <Skeleton className="h-5 w-80 max-w-full" />
    </div>
  );
}
